import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { EyeOff, FileSpreadsheet, Printer } from 'lucide-react';
import type { EnablementProgram, ImpactSummary, ReportAction, ReportChallenge, StrategyConsolidatedReport, StrategyKpiDeep, StrategyNode, StrategyScheduleRow } from '@mnc/contracts';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, EmptyState, PageHeader, SearchableSelect, TableSkeleton, cn } from '../../components/ui';
import { LevelBadge, ProgressBar, StatusBadge, StatusDistribution, fmtDate, nodeTitle, trackTone, useLevelLabel, useStrategyTree } from '../strategy/shared';
import { fmtNumber } from '../stakeholders/shared';
import { Figure, ReportSection, ReportTable, useReportColumns, type ReportColumn } from './shared';
import { REPORT_SECTIONS, hiddenSections, parseStrategyReportSearch, type ReportSection as SectionCode, type StrategyReportSearch } from './search';

type Issue = ({ type: 'challenge' } & ReportChallenge) | ({ type: 'action' } & ReportAction);
type ImpactRow = ImpactSummary | { unit_code: '__total__'; total: number | null };

/**
 * Strategy consolidated report (phase-1-scope §1.6). One page for screen and paper: the print stylesheet hides the
 * chrome, and the section and column toggles decide what is printed and what the Excel export contains. Every
 * block comes from the same service as the screen it mirrors, so the figures equal the pages behind them.
 */
export function StrategyReportPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const { me, config, canAction } = useAuth();
  const navigate = useNavigate();
  const search = parseStrategyReportSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const hidden = hiddenSections(search);
  const tree = useStrategyTree();
  const levelLabel = useLevelLabel(tree.data?.config);
  const [exporting, setExporting] = useState(false);
  const report = useQuery({
    queryKey: ['reports', 'strategy-consolidated', search.node ?? 'root'],
    queryFn: () => api.get<StrategyConsolidatedReport>(`/api/reports/strategy-consolidated${qs({ node_id: search.node })}`),
  });
  const data = report.data;

  const setSearch = (patch: Partial<StrategyReportSearch>) => {
    const next: Record<string, unknown> = { ...search, ...patch };
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    void navigate({ to: '/reports/strategy-consolidated', search: next as StrategyReportSearch });
  };
  const toggleSection = (code: SectionCode) => {
    const next = new Set(hidden);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSearch({ hide: [...next].join(',') || undefined });
  };

  const name = (en: string | null | undefined, ar: string | null | undefined) => pick(locale, en ?? '', ar ?? '');
  const num = (n: number | null | undefined) => (n === null || n === undefined ? '—' : fmtNumber(n, locale));
  const pct = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${fmtNumber(n, locale)}%`);
  const yesNo = (v: boolean) => t(v ? 'app.yes' : 'app.no');
  const restrictedBadge = <Badge tone="warn">{t('reports.restricted')}</Badge>;

  // Column keys equal the API's sheet column keys, so the visible set is sent to the export unchanged.
  const levelColumns: Array<ReportColumn<StrategyNode>> = [
    { key: 'code', label: t('strategy.code'), required: true, render: (n) => <code>{n.code}</code> },
    { key: 'level', label: t('reports.col.level'), render: (n) => <LevelBadge level={n.level} config={data?.strategy.config} /> },
    { key: 'name', label: t('reports.col.name'), required: true, render: (n) => <Link to="/strategy/nodes/$id" params={{ id: n.id }} className="hover:underline" dir="auto">{name(n.name_en, n.name_ar)}</Link> },
    { key: 'status', label: t('strategy.status'), render: (n) => <StatusBadge status={n.rollup.status} /> },
    { key: 'progress', label: t('reports.col.kpiProgress'), numeric: true, render: (n) => (n.rollup.progress === null ? '—' : <span className="inline-flex items-center gap-2"><ProgressBar value={n.rollup.progress} className="no-print w-16" />{pct(n.rollup.progress)}</span>) },
    { key: 'kpi_count', label: t('reports.col.kpis'), numeric: true, render: (n) => num(n.rollup.kpi_count) },
    { key: 'tasks_open', label: t('reports.col.tasksOpen'), numeric: true, render: (n) => num(n.rollup.tasks_open) },
    { key: 'tasks_overdue', label: t('reports.col.tasksOverdue'), numeric: true, render: (n) => <span className={cn(n.rollup.tasks_overdue > 0 && 'font-semibold text-red-600')}>{num(n.rollup.tasks_overdue)}</span> },
    { key: 'challenges_open', label: t('reports.col.challengesOpen'), numeric: true, render: (n) => num(n.rollup.challenges_open) },
    { key: 'start_date', label: t('strategy.startDate'), render: (n) => fmtDate(n.start_date, locale) },
    { key: 'end_date', label: t('strategy.endDate'), render: (n) => fmtDate(n.end_date, locale) },
    { key: 'owner', label: t('strategy.owner'), render: (n) => (n.owner ? <span dir="auto">{name(n.owner.display_name_en, n.owner.display_name_ar)}</span> : '—') },
  ];
  const kpiColumns: Array<ReportColumn<StrategyKpiDeep>> = [
    { key: 'node_code', label: t('reports.col.itemCode'), render: (k) => <code>{k.node_code}</code> },
    { key: 'node', label: t('reports.col.item'), render: (k) => <span dir="auto">{name(k.node_name_en, k.node_name_ar)}</span> },
    { key: 'title', label: t('reports.col.kpi'), required: true, render: (k) => <span dir="auto">{name(k.title_en, k.title_ar)}</span> },
    { key: 'group', label: t('strategy.kpi.group'), render: (k) => t(`strategy.kpi.groups.${k.kpi_group}`) },
    { key: 'unit', label: t('strategy.kpi.unit'), render: (k) => name(k.unit_name_en, k.unit_name_ar) || '—' },
    { key: 'baseline', label: t('strategy.kpi.baseline'), numeric: true, render: (k) => num(k.baseline_value) },
    { key: 'target', label: t('strategy.kpi.target'), numeric: true, render: (k) => num(k.target_value) },
    { key: 'current', label: t('reports.col.current'), numeric: true, render: (k) => num(k.current_value) },
    { key: 'progress', label: t('strategy.kpi.progress'), numeric: true, render: (k) => pct(k.progress) },
    { key: 'expected', label: t('reports.col.expected'), numeric: true, render: (k) => pct(k.expected_progress) },
    { key: 'track', label: t('reports.col.track'), render: (k) => <Badge tone={trackTone(k.track)}>{t(`reports.track.${k.track}`)}</Badge> },
    { key: 'last_log', label: t('reports.col.lastLog'), render: (k) => fmtDate(k.last_log_date, locale) },
  ];
  const scheduleColumns: Array<ReportColumn<StrategyScheduleRow>> = [
    { key: 'kind', label: t('reports.col.type'), render: (r) => t(`reports.scheduleKind.${r.kind}`) },
    { key: 'code', label: t('strategy.code'), required: true, render: (r) => <code>{r.code}</code> },
    { key: 'name', label: t('reports.col.name'), required: true, render: (r) => <span dir="auto">{name(r.name_en, r.name_ar)}</span> },
    { key: 'start', label: t('strategy.startDate'), render: (r) => fmtDate(r.start, locale) },
    { key: 'end', label: t('strategy.endDate'), render: (r) => fmtDate(r.end, locale) },
    { key: 'status', label: t('strategy.status'), render: (r) => <StatusBadge status={r.status} /> },
    { key: 'progress', label: t('strategy.kpi.progress'), numeric: true, render: (r) => pct(r.progress) },
    { key: 'overdue', label: t('reports.col.overdue'), render: (r) => (r.is_overdue ? <Badge tone="danger">{t('app.yes')}</Badge> : t('app.no')) },
  ];
  const issueColumns: Array<ReportColumn<Issue>> = [
    { key: 'type', label: t('reports.col.type'), required: true, render: (r) => <Badge tone={r.type === 'challenge' ? 'warn' : 'info'}>{t(`reports.issueType.${r.type}`)}</Badge> },
    { key: 'node_code', label: t('reports.col.itemCode'), render: (r) => <code>{r.node_code}</code> },
    { key: 'node', label: t('reports.col.item'), render: (r) => <span dir="auto">{name(r.node_name_en, r.node_name_ar)}</span> },
    { key: 'title', label: t('reports.col.title'), required: true, render: (r) => <span dir="auto">{name(r.title_en, r.title_ar)}</span> },
    { key: 'severity', label: t('strategy.challenge.severity'), render: (r) => (r.type === 'challenge' ? <Badge tone={r.severity === 'high' ? 'danger' : r.severity === 'medium' ? 'warn' : 'neutral'}>{t(`strategy.challenge.severities.${r.severity}`)}</Badge> : '—') },
    { key: 'due_date', label: t('reports.col.due'), render: (r) => (r.type === 'action' ? fmtDate(r.due_date, locale) : '—') },
    { key: 'overdue', label: t('reports.col.overdue'), render: (r) => (r.type === 'action' ? (r.is_overdue ? <Badge tone="danger">{t('app.yes')}</Badge> : yesNo(false)) : '—') },
    { key: 'owner', label: t('strategy.owner'), render: (r) => (r.owner ? <span dir="auto">{name(r.owner.display_name_en, r.owner.display_name_ar)}</span> : '—') },
    { key: 'description', label: t('reports.col.description'), render: (r) => <span dir="auto" className="whitespace-pre-line text-xs text-neutral-600 dark:text-neutral-300">{r.description ?? ''}</span> },
  ];
  const units = useMemo(() => {
    const seen = new Map<string, ImpactSummary>();
    for (const p of data?.programs ?? []) for (const i of p.impacts) if (!seen.has(i.unit_code)) seen.set(i.unit_code, i);
    return [...seen.values()];
  }, [data?.programs]);
  const programColumns: Array<ReportColumn<EnablementProgram>> = [
    { key: 'program', label: t('reports.col.program'), required: true, render: (p) => <Link to="/stakeholders/programs/$id" params={{ id: p.id }} className="hover:underline" dir="auto">{name(p.name_en, p.name_ar)}</Link> },
    { key: 'stakeholder', label: t('reports.col.stakeholder'), render: (p) => <span dir="auto">{name(p.stakeholder.name_en, p.stakeholder.name_ar)}</span> },
    { key: 'strategy_node', label: t('reports.col.item'), render: (p) => (p.strategy_node ? <span dir="auto"><code>{p.strategy_node.code}</code> {name(p.strategy_node.name_en, p.strategy_node.name_ar)}</span> : '—') },
    { key: 'start_date', label: t('strategy.startDate'), render: (p) => fmtDate(p.start_date, locale) },
    { key: 'end_date', label: t('strategy.endDate'), render: (p) => fmtDate(p.end_date, locale) },
    { key: 'enrolment_count', label: t('reports.col.enrolled'), numeric: true, render: (p) => num(p.enrolment_count) },
    ...units.map((u): ReportColumn<EnablementProgram> => ({
      key: `unit_${u.unit_code}`,
      label: name(u.unit_name_en, u.unit_name_ar),
      numeric: true,
      render: (p) => {
        const hit = p.impacts.find((i) => i.unit_code === u.unit_code);
        if (!hit) return '';
        return hit.total === null ? restrictedBadge : num(hit.total);
      },
    })),
  ];
  const impactColumns: Array<ReportColumn<ImpactRow>> = [
    { key: 'unit', label: t('reports.col.unit'), required: true, render: (r) => (r.unit_code === '__total__' ? <strong>{t('stakeholders.stats.totalEconomicValue')}</strong> : <span dir="auto">{name((r as ImpactSummary).unit_name_en, (r as ImpactSummary).unit_name_ar)}</span>) },
    { key: 'aggregation', label: t('reports.col.aggregation'), render: (r) => (r.unit_code === '__total__' ? '' : t(`reports.aggregation.${(r as ImpactSummary).aggregation}`)) },
    { key: 'total', label: t('reports.col.total'), required: true, numeric: true, render: (r) => (r.unit_code !== '__total__' && r.total === null ? restrictedBadge : <span className={cn(r.unit_code === '__total__' && 'font-semibold')}>{num(r.total)}</span>) },
    { key: 'samples', label: t('reports.col.samples'), numeric: true, render: (r) => (r.unit_code === '__total__' ? '' : num((r as ImpactSummary).sample_count)) },
    { key: 'component', label: t('reports.col.component'), render: (r) => (r.unit_code === '__total__' ? '' : yesNo((r as ImpactSummary).is_total_value_component)) },
  ];

  const cols = {
    levels: useReportColumns('levels', levelColumns),
    kpis: useReportColumns('kpis', kpiColumns),
    schedule: useReportColumns('schedule', scheduleColumns),
    issues: useReportColumns('issues', issueColumns),
    programs: useReportColumns('programs', programColumns),
    impact: useReportColumns('impact', impactColumns),
  };

  const scopeOptions = useMemo(
    () => (tree.data?.nodes ?? []).map((n) => ({ value: n.id, label: nodeTitle(locale, n), description: levelLabel(n.level) })),
    [tree.data, locale, levelLabel],
  );
  const shown = (code: SectionCode) => Boolean(data?.sections.includes(code)) && !hidden.has(code);

  const exportXlsx = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const sections = data.sections.filter((s) => !hidden.has(s as SectionCode));
      const columns = (Object.entries(cols) as Array<[string, { visible: Set<string>; customised: boolean }]>)
        .filter(([, c]) => c.customised)
        .flatMap(([sheet, c]) => [...c.visible].map((key) => `${sheet}.${key}`));
      await api.download(`/api/reports/strategy-consolidated/export${qs({ node_id: data.strategy.node.id, locale: locale === 'ar' ? 'ar' : 'en', sections: sections.length === data.sections.length ? undefined : sections.join(','), columns: columns.join(',') || undefined })}`);
      toast.success(t('reports.exported'));
    } catch (e) {
      toast.error(describeError(e, t));
    } finally {
      setExporting(false);
    }
  };

  if (report.isError) return <EmptyState>{t('reports.noStrategy')}</EmptyState>;
  if (!data) return <TableSkeleton />;

  const s = data.strategy;
  const sum = s.summary;
  const po = data.programs_overview;
  const statusCounts: Record<string, number> = {};
  for (const n of s.nodes) statusCounts[n.rollup.status] = (statusCounts[n.rollup.status] ?? 0) + 1;
  const issues: Issue[] = [...data.challenges.map((c): Issue => ({ type: 'challenge', ...c })), ...data.actions.map((a): Issue => ({ type: 'action', ...a }))];
  const impactRows: ImpactRow[] = po ? [...po.impact_totals, { unit_code: '__total__', total: po.total_value.combined }] : [];
  const generatedAt = new Date(data.generated_at).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: config?.timezone });

  return (
    <div className="report-page">
      <div className="no-print">
        <PageHeader
          title={t('reports.strategy.title')}
          subtitle={t('reports.strategy.subtitle')}
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-secondary" onClick={() => window.print()}><Printer size={16} /> {t('reports.print')}</button>
              {canAction('reports.export') && <button type="button" className="btn-primary" disabled={exporting} onClick={() => void exportXlsx()}><FileSpreadsheet size={16} /> {t(exporting ? 'reports.exporting' : 'reports.exportXlsx')}</button>}
            </div>
          }
        />
        <div className="card mb-4 flex flex-wrap items-end gap-3 p-3">
          <div className="w-80 max-w-full">
            <div className="label">{t('reports.scope')}</div>
            <SearchableSelect value={s.node.id} onChange={(v) => setSearch({ node: (v as string) || undefined })} options={scopeOptions} placeholder={t('reports.scope')} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {REPORT_SECTIONS.filter((code) => data.sections.includes(code)).map((code) => (
              <button key={code} type="button" onClick={() => toggleSection(code)} className={cn('rounded-full border px-2.5 py-1 text-xs', hidden.has(code) ? 'border-neutral-300 text-neutral-400 line-through dark:border-neutral-600' : 'border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-700/20 dark:text-brand-100')} aria-pressed={!hidden.has(code)}>
                {t(`reports.sections.${code}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* The report header is part of the document: it is what identifies a printed copy. */}
      <header className="report-header card mb-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-neutral-500" dir="auto">{name(config?.display_name_en, config?.display_name_ar)}</div>
            <h1 className="text-xl font-semibold">{t('reports.strategy.title')}</h1>
            <div className="mt-1 text-sm" dir="auto">
              <LevelBadge level={s.node.level} config={s.config} /> <code>{s.node.code}</code> {name(s.node.name_en, s.node.name_ar)}
            </div>
            {s.ancestors.length > 0 && <div className="mt-0.5 text-xs text-neutral-500" dir="auto">{s.ancestors.map((a) => name(a.name_en, a.name_ar)).join(' › ')}</div>}
          </div>
          <div className="text-end text-xs text-neutral-500">
            <div>{t('reports.generatedAt', { when: generatedAt })}</div>
            <div dir="auto">{t('reports.generatedBy', { who: name(me?.display_name_en, me?.display_name_ar) })}</div>
            {sum.date_range.start && <div>{fmtDate(sum.date_range.start, locale)} – {fmtDate(sum.date_range.end, locale)}</div>}
          </div>
        </div>
        <p className="mt-3 text-xs text-neutral-500">{t('reports.visibilityNote')}</p>
        {data.masking.restricted_values_hidden && (
          <p className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
            <EyeOff size={14} /> {t('reports.maskingNote')}
          </p>
        )}
      </header>

      {shown('summary') && (
        <ReportSection id="summary" title={t('reports.sections.summary')}>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {sum.by_level.map((lv) => <Figure key={lv.level} value={num(lv.total)} label={levelLabel(lv.level, true)} />)}
            <Figure value={num(sum.kpis.total)} label={t('reports.fig.kpis')} sub={t('reports.fig.kpiTracks', { achieved: sum.kpis.achieved, onTrack: sum.kpis.on_track, behind: sum.kpis.behind })} tone={sum.kpis.behind > 0 ? 'warn' : 'ok'} />
            <Figure value={num(sum.tasks.open)} label={t('reports.fig.tasksOpen')} sub={t('reports.fig.tasksSub', { overdue: sum.tasks.overdue, done: sum.tasks.done })} tone={sum.tasks.overdue > 0 ? 'danger' : 'ok'} />
            <Figure value={num(sum.challenges.open)} label={t('reports.fig.challengesOpen')} sub={t('reports.fig.challengesSub', { high: sum.challenges.high, actions: sum.actions.open })} tone={sum.challenges.high > 0 ? 'danger' : 'ok'} />
            <Figure value={num(sum.team_members)} label={t('reports.fig.team')} />
            {po && <Figure value={num(po.programs_total)} label={t('reports.fig.programs')} sub={t('reports.fig.programsSub', { stakeholders: po.stakeholders_with_programs })} />}
            {po && <Figure value={num(po.enrolled_beneficiaries)} label={t('stakeholders.stats.beneficiariesFromPrograms')} />}
            {po && po.total_value.combined !== null && <Figure value={num(po.total_value.combined)} label={t('stakeholders.stats.totalEconomicValue')} sub={po.total_value.units.map((u) => name(u.name_en, u.name_ar)).join(' + ')} />}
          </div>
          <StatusDistribution counts={statusCounts} className="mt-3" />
        </ReportSection>
      )}

      {shown('levels') && (
        <ReportSection id="levels" title={t('reports.sections.levels')} subtitle={t('reports.levelsHint')}>
          <ReportTable columns={levelColumns} rows={s.nodes} visible={cols.levels.visible} onToggle={cols.levels.toggle} rowKey={(n) => n.id} empty={t('strategy.noNodes')} />
        </ReportSection>
      )}
      {shown('kpis') && (
        <ReportSection id="kpis" title={t('reports.sections.kpis')} subtitle={t('reports.kpisHint')}>
          <ReportTable columns={kpiColumns} rows={s.kpis} visible={cols.kpis.visible} onToggle={cols.kpis.toggle} rowKey={(k) => k.id} empty={t('strategy.kpi.none')} />
        </ReportSection>
      )}
      {shown('schedule') && (
        <ReportSection id="schedule" title={t('reports.sections.schedule')}>
          <ReportTable columns={scheduleColumns} rows={s.schedule} visible={cols.schedule.visible} onToggle={cols.schedule.toggle} rowKey={(r) => `${r.kind}-${r.id}`} empty={t('reports.noSchedule')} />
        </ReportSection>
      )}
      {shown('issues') && (
        <ReportSection id="issues" title={t('reports.sections.issues')} subtitle={t('reports.issuesHint')}>
          <ReportTable columns={issueColumns} rows={issues} visible={cols.issues.visible} onToggle={cols.issues.toggle} rowKey={(r) => `${r.type}-${r.id}`} empty={t('reports.noIssues')} />
        </ReportSection>
      )}
      {shown('programs') && data.programs && (
        <ReportSection id="programs" title={t('reports.sections.programs')} subtitle={t('reports.programsHint')}>
          <ReportTable columns={programColumns} rows={data.programs} visible={cols.programs.visible} onToggle={cols.programs.toggle} rowKey={(p) => p.id} empty={t('reports.noPrograms')} />
        </ReportSection>
      )}
      {shown('impact') && po && (
        <ReportSection id="impact" title={t('reports.sections.impact')} subtitle={t('reports.impactHint')}>
          <ReportTable columns={impactColumns} rows={impactRows} visible={cols.impact.visible} onToggle={cols.impact.toggle} rowKey={(r) => r.unit_code} empty={t('reports.noImpact')} />
        </ReportSection>
      )}
      {shown('beneficiaries') && data.beneficiaries && (
        <ReportSection id="beneficiaries" title={t('reports.sections.beneficiaries')} subtitle={t('reports.beneficiariesHint')}>
          <div className="mb-3 grid gap-2 sm:grid-cols-3">
            <Figure value={num(data.beneficiaries.total)} label={t('reports.fig.beneficiaries')} />
            <Figure value={num(data.beneficiaries.organisations)} label={t('beneficiaries.kindsPlural.organisation')} />
            <Figure value={num(data.beneficiaries.individuals)} label={t('beneficiaries.kindsPlural.individual')} />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {([['byType', data.beneficiaries.by_type], ['byRegion', data.beneficiaries.by_region], ['funnel', data.beneficiaries.funnel]] as const).map(([key, rows]) => (
              <div key={key}>
                <h3 className="mb-1 text-sm font-medium">{t(`beneficiaries.${key}`)}</h3>
                <table className="report-table w-full text-sm">
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.code} className="border-b border-neutral-100 dark:border-neutral-800">
                        <td className="px-2 py-1" dir="auto">{name(r.name_en, r.name_ar)}</td>
                        <td className="px-2 py-1 text-end tabular-nums">{num(r.count)}</td>
                      </tr>
                    ))}
                    {rows.length === 0 && <tr><td className="px-2 py-1 text-neutral-400">{t('app.none')}</td></tr>}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </ReportSection>
      )}
    </div>
  );
}
